/**
 * 資金移動(口座間振替・type='transfer')。
 *
 * 口座間の付け替えは売上でも経費でもない。**PLには一切含めない**
 * (summarizeTransactions で type='transfer' を除外している)。
 * CF上は両口座に出金/入金として現れる。
 *
 * 実装: transfer の取引1件と、出金側・入金側の cash_entries 2件を
 * writeBatch で同時に作り、相互にIDを持たせる。
 */
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fiscalYearOf, toMonth } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import { writeAuditLog } from '@core/lib/auditLog';
import { newCashEntryId } from './cashRepo';
import type { DateStr, Yen } from '@core/lib/types';

export type TransferInput = {
  companyId: string;
  fromCashAccountId: string;
  toCashAccountId: string;
  date: DateStr;
  amount: Yen;
  description: string;
  /** true=実行済み(settled) / false=予定(planned) */
  settled: boolean;
};

export async function createTransfer(params: {
  input: TransferInput;
  userId: string;
  fiscalStartMonth: number;
}): Promise<void> {
  const { input, userId, fiscalStartMonth } = params;
  if (!isValidAmount(input.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!input.fromCashAccountId || !input.toCashAccountId) {
    throw new Error('移動元と移動先の口座を選んでください');
  }
  if (input.fromCashAccountId === input.toCashAccountId) {
    throw new Error('移動元と移動先が同じ口座です');
  }
  if (!input.date) throw new Error('日付を入力してください');

  const db = getDb();
  const batch = writeBatch(db);
  const txId = doc(collection(db, 'transactions')).id;
  const outId = newCashEntryId();
  const inId = newCashEntryId();
  const status = input.settled ? 'settled' : 'planned';
  const description = input.description || '資金移動';

  const entryBase = {
    /** 振替の識別用(CF画面の表示に使う) */
    kind: 'transfer',
    amount: input.amount,
    status,
    description,
    counterparty: null,
    transaction_id: txId,
    import_batch_id: null,
    dedup_hash: null,
    matched_at: null,
    matched_by: null,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  batch.set(doc(db, 'cash_entries', outId), {
    ...entryBase,
    cash_account_id: input.fromCashAccountId,
    date: input.date,
    direction: 'out',
  });

  batch.set(doc(db, 'cash_entries', inId), {
    ...entryBase,
    cash_account_id: input.toCashAccountId,
    date: input.date,
    direction: 'in',
  });

  // 取引としても1件残す(監査と会社軸の集計のため)。PLからは type で除外される
  batch.set(doc(db, 'transactions', txId), {
    type: 'transfer',
    status: 'paid',
    code: null,
    doc_type: null,
    payer: null,
    vendor_id: null,
    invoice_no: null,
    invoice_date: null,
    company_id: input.companyId,
    department_id: 'honbu', // 資金移動は本部(経理)の操作として記録する
    account_id: 'bank-deposit',
    sub_account_id: 'bank-deposit--1',
    hierarchy_node_id: null,

    amount: input.amount,
    applied_amount: input.amount,
    tax_mode: 'inclusive',
    tax_amount: null,

    posting_date: input.date,
    posting_month: toMonth(input.date),
    payment_date: input.date,
    payment_month: toMonth(input.date),
    fiscal_year: fiscalYearOf(input.date, fiscalStartMonth),
    due_date: null,

    counterparty: null,
    description,
    payment_method: 'bank_transfer',
    evidence_type: null,

    attachments: [],
    receipts: [],

    is_internal: false,
    counterparty_department_id: null,

    approval_rule_id: '',
    approval_snapshot: [],
    current_step: 0,
    approvals: [],

    applied_at: null,
    applied_by: null,
    manager_checked_at: null,
    manager_checked_by: null,
    approved_at: null,
    approved_by: null,
    paid_at: serverTimestamp(),
    paid_by: userId,
    completed_at: null,
    completed_by: null,
    submitted_at: null,
    submitted_by: null,
    confirmed_at: null,
    confirmed_by: null,
    rejected_at: null,
    rejected_by: null,
    reject_reason: null,
    rejected_from_status: null,

    source: 'manual',
    cash_entry_id: outId,
    recurring_cost_id: null,

    created_by: userId,
    created_at: serverTimestamp(),
    updated_by: userId,
    updated_at: serverTimestamp(),
  });

  await batch.commit();

  await writeAuditLog({
    targetType: 'transaction',
    targetId: txId,
    action: 'transfer-create',
    by: userId,
    after: {
      from: input.fromCashAccountId,
      to: input.toCashAccountId,
      amount: input.amount,
      date: input.date,
    },
  });
}
